import { Edit, Delete, NorthEast, SouthWest } from "@mui/icons-material";
import { Box } from "@mui/material";
import { TableActions } from "../../components/v3";
import { TransactionTypes, getBalanceColor } from "../../utils";

/**
 * Route paths for the administration module.
 * Single source of truth — reuse these instead of hard-coding `/administration...` or `/user...` strings.
 */
export const ADMINISTRATION_ROUTES = {
  root: "/administration",
  addRole: "/administration/roles/add",
  editRole: "/administration/roles/edit/:id",
  userDetails: "/user/:id",
} as const;

export const getUserTableColumns = (handleActions, userType, hasUserEdit, hasUserDelete, currentUserId) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "firstName", headerName: "Name", flex: 1.5, sortable: true },
    { field: "username", headerName: "user name", flex: 1.5, sortable: true },
    { field: "primaryPhone", headerName: "Mobile", flex: 1.5, sortable: false },
    { field: "primaryEmail", headerName: "Email", flex: 1.5, sortable: true },
    { field: "roleId", headerName: "Role", flex: 1.5, sortable: false },
    {
      field: "formattedBalance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const rawBalance = params.row.balance;

        return (
          <Box
            sx={{
              color: getBalanceColor(parseFloat(rawBalance)),
              display: "flex",
              alignItems: "center",
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
  ];

  if (hasUserEdit || hasUserDelete) {
    headers.push({
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const actions = [];
        const isCurrentUser = params.row.id === currentUserId;

        if (hasUserEdit && !isCurrentUser) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }

        if (hasUserDelete && userType === "ADMIN" && !isCurrentUser) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }

        return <TableActions item={params} actions={actions} />;
      },
    });
  }

  return headers;
};

export const getUserActivityTableColumns = (handleActions, canEdit, canDelete, selectedFilter) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "date", flex: 1, sortable: true, sortingOrder: ["desc", "asc", null] },
    { field: "fromAccountName", headerName: "from", flex: 1, sortable: false },
    { field: "toAccountName", headerName: "to", flex: 1, sortable: false },
    { field: "project", headerName: "project", flex: 1, sortable: false },
    { field: "description", headerName: "description", flex: 1.5, sortable: false },
    { field: "activityType", headerName: "type", flex: 1, sortable: false },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const value = params.value;
        const { color, transactionType } = params.row;

        const showArrow = transactionType === "transaction";
        const IconComponent = showArrow ? (color === "green" ? SouthWest : color === "red" ? NorthEast : null) : null;

        if (!value) return "-";
        return (
          <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>
            {IconComponent && <IconComponent fontSize="small" />}₹ {Number(value).toFixed(2)}
          </span>
        );
      },
    },
  ];
  if (canEdit || canDelete) {
    headers.push({
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { transactionType } = params.row;
        const actions = [];
        if ([TransactionTypes.ACCOUNT, TransactionTypes.WORKER, TransactionTypes.VENDOR].includes(transactionType)) {
          if (canEdit) {
            actions.push({
              icon: Edit,
              type: "edit",
              label: "Edit",
              onClick: handleActions,
            });
          }
          if (canDelete) {
            actions.push({
              icon: Delete,
              type: "delete",
              label: "Delete",
              onClick: handleActions,
            });
          }
          return <TableActions item={params} actions={actions} />;
        }
      },
    });
  }

  return selectedFilter === "expense"
    ? headers.filter((col) => col.field !== "fromAccountName" && col.field !== "toAccountName" && col.field !== "actions")
    : headers;
};
